/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowCircleLeft, ArrowCircleRight, Delete } from "@mui/icons-material";
import { Tab, Tabs, Button, IconButton, Stack, TextField, Select, MenuItem } from "@mui/material";
import { AddCircleOutline as AddCircleOutlineIcon } from "@mui/icons-material";
import { v4 as uuidv4 } from "uuid";
import isEqual from "react-fast-compare";
import { indexOf } from "lodash";
import LabelledField from "components/labelled-field";
import { Logger } from "cms/utils/log/logger";
import { isArrayNotEmpty, isArray } from "utils/empty-exists";
import { useUpdateComponent } from "cms/components/hooks/use-update-component";

import { ServiceListTabContent } from "./tab_content";

// TODO: move to /styles (regular css)
import styles from "cms/components/components/tab-group/edit/index.module.css";

const IconLinkTabsComponentEditor = () => {

    const { component, update } = useUpdateComponent();
    const _tabs = component?.tabs;
    const title = component?.title;
    const tab_position = component?.tab_position || "top";

    const { t } = useTranslation();
    const itemTabs = isArray(_tabs) ? _tabs : [];
    const firstTabGuid = isArrayNotEmpty(_tabs) ? _tabs[0]?.guid : undefined;
    const defaultTabs = new Map([...itemTabs].map((tab) => [tab.guid, tab]));

    const [tabs, setTabs] = useState(defaultTabs);
    const [selectedTab, setSelectedTab] = useState(firstTabGuid);

    // Initialize tab_position to "top" if it doesn't exist
    useEffect(() => {
        if (!component?.tab_position) {
            update("tab_position", "top");
        }
    }, [component?.tab_position, update]);

    const onChangeTitle = (event) => {
        const title = event.target.value;
        update("title", title);
    };

    const onChangeTabPosition = (event) => {
        const position = event.target.value || "top";
        update("tab_position", position);
    };

    function onSelectTab (event, guid) {
        setSelectedTab(guid);
        if (isArrayNotEmpty(_tabs)) {
            const tab = _tabs.find((tab) => tab.guid === guid);
            const { title } = tab || {};
            Logger.debug(`${guid} - ${title}`);
            setSelectedTab(guid);
        }
    }

    const addNewTab = () => {
        let updatedTabs = new Map(tabs);
        const guid = uuidv4();
        setSelectedTab(guid);
        updatedTabs.set(guid, { title: "[New]", guid, icon_links: [] });
        setTabs(updatedTabs);
        const tabsArray = Array.from(updatedTabs.values());
        update("tabs", tabsArray);
    };

    function removeTab (guid) {
        const updatedTabs = new Map(tabs);
        const originalKeys = [...tabs.keys()];
        const guidKeyIdx = indexOf(originalKeys, guid);
        const isFirstTab = guidKeyIdx > 0;
        const selectedGuid = isFirstTab
            ? originalKeys[guidKeyIdx - 1]
            : originalKeys[guidKeyIdx + 1];
        onSelectTab(null, selectedGuid);
        updatedTabs.delete(guid);
        setTabs(updatedTabs);
        const tabsArray = Array.from(updatedTabs.values());
        update("tabs", tabsArray);
    }

    function updateTab (guid, prevTab, nextTab) {
        if (isEqual(prevTab, nextTab)) {
            //
        } else {
            const updatedTabs = new Map(tabs);
            updatedTabs.set(guid, nextTab);
            setTabs(updatedTabs);
            const tabsArray = Array.from(updatedTabs.values());
            update("tabs", tabsArray);
        }
    }

    function _shiftTab (guid, increment) {
        const keys = [...tabs.keys()];
        const guidIdx = indexOf(keys, guid);
        const newPosition = guidIdx + increment;

        let swappedKeys = [...keys];

        if (newPosition < 0 || newPosition > swappedKeys.length - 1) {
            Logger.debug(`position outside of range ${newPosition}, range 0 - ${swappedKeys.length - 1}`);
            return false;
        }
        swappedKeys[newPosition] = keys[guidIdx];
        swappedKeys[guidIdx] = keys[newPosition];
        swappedKeys = swappedKeys.filter(function (element) {
            return element !== undefined;
        });
        const map = new Map();
        swappedKeys.reduce((acc, key) => {
            acc[key] = tabs.get(key);
            map.set(key, tabs.get(key));
            return acc;
        }, {});
        setTabs(map);
        const tabsArray = Array.from(map.values());
        update("tabs", tabsArray);
    }

    function moveTabLeft (guid) {
        _shiftTab(guid, -1);
    }

    function moveTabRight (guid) {
        _shiftTab(guid, 1);
    }

    function getEditorIconButtons (guid) {
        const iconButtons = [
            { fn: removeTab, ariaLabel: "delete", icon: <Delete /> },
            { fn: moveTabLeft, ariaLabel: "move left", icon: <ArrowCircleLeft /> },
            { fn: moveTabRight, ariaLabel: "move right", icon: <ArrowCircleRight /> },
        ];

        return iconButtons.map((button, idx) => {
            return (
                <IconButton
                    key={idx}
                    aria-label={button.ariaLabel}
                    onClick={() => {
                        button.fn(guid);
                    }}
                >
                    {button.icon}
                </IconButton>
            );
        });
    }

    return (
        <div className="simplicity-editor-frame">
            {/*  Title */}
            <LabelledField label={t("commons.heading")}>
                <TextField
                    id="service_list_tabs_title"
                    fullWidth={true}
                    value={title}
                    placeholder={t("commons.addAHeading")}
                    onChange={onChangeTitle}
                />
            </LabelledField>

            {/* Tab Position */}
            <LabelledField label={t("commons.defaultTabPosition")}>
                <Select
                    id="icon-link-tabs-position"
                    value={tab_position || "top"}
                    onChange={onChangeTabPosition}
                    fullWidth
                >
                    <MenuItem value="top">Top</MenuItem>
                    <MenuItem value="left">Left</MenuItem>
                </Select>
            </LabelledField>

            {!tabs && <>{t("Components.editThisItemToAddSomeTabs")}</>}
            {tabs && (
                <div className="panel-group tabs" data-group-type="tabs" style={styles.tabs}>
                    <div style={{ display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                        <Button
                            className="accordion-component-add-section-button"
                            size="medium"
                            startIcon={<AddCircleOutlineIcon />}
                            color={"primary"}
                            target="warning"
                            onClick={addNewTab}
                        >
                            Add Tab
                        </Button>
                    </div>

                    <Tabs
                        value={selectedTab}
                        onChange={onSelectTab}
                        variant="scrollable"
                        scrollButtons="auto"
                    >
                        {tabs &&
                            [...tabs.keys()].map((guid, index) => {
                                const tab = tabs?.get(guid);
                                const key = `${guid}${index}`;
                                return (
                                    <Tab
                                        key={key}
                                        value={guid}
                                        label={tab?.title}
                                    />
                                );
                            })
                        }
                    </Tabs>

                    <Stack direction="column" justifyContent="flex-start" mb={4}>
                        <Stack direction="row" justifyContent="flex-end">
                            {getEditorIconButtons(selectedTab)}
                        </Stack>

                        {
                            [...tabs.keys()].map((id, index) => {
                                const tab = tabs?.get(id);
                                const { guid } = tab || {};
                                const key = index + guid;
                                const isCurrent = selectedTab === guid;
                                return (
                                    <ServiceListTabContent
                                        onClick={(event) => onSelectTab(event, guid)}
                                        key={key}
                                        isCurrent={isCurrent}
                                        tab={tab}
                                        updateTab={updateTab}
                                    />
                                );
                            })}
                    </Stack>
                </div>
            )}
        </div>
    );
};

IconLinkTabsComponentEditor.displayName = "IconLinkTabsComponentEditor";
export { IconLinkTabsComponentEditor };
